/**
 * Rate Limiter Utility for Infura API calls
 * Prevents 429 (Too Many Requests) errors by throttling API calls
 */

interface RateLimiterConfig {
  maxRequestsPerSecond: number;
  burstLimit?: number;
  retryAttempts?: number;
  retryDelayBase?: number; // Base delay for exponential backoff in ms
}

interface QueuedRequest {
  execute: () => Promise<any>;
  resolve: (value: any) => void;
  reject: (error: any) => void;
  timestamp: number;
  retryCount: number;
}

export class RateLimiter {
  private config: Required<RateLimiterConfig>;
  private requestQueue: QueuedRequest[] = [];
  private recentRequests: number[] = [];
  private isProcessing = false;
  private lastRequestTime = 0;

  constructor(config: RateLimiterConfig) {
    this.config = {
      maxRequestsPerSecond: config.maxRequestsPerSecond,
      burstLimit: config.burstLimit || config.maxRequestsPerSecond,
      retryAttempts: config.retryAttempts || 3,
      retryDelayBase: config.retryDelayBase || 1000,
    };
  }

  /**
   * Add a request to the rate-limited queue
   */
  async executeWithLimit<T>(requestFn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const queuedRequest: QueuedRequest = {
        execute: requestFn,
        resolve,
        reject,
        timestamp: Date.now(),
        retryCount: 0,
      };

      this.requestQueue.push(queuedRequest);
      this.processQueue();
    });
  }

  /**
   * Process the queue of pending requests
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      const now = Date.now();

      // Clean up old request timestamps (older than 1 second)
      this.recentRequests = this.recentRequests.filter(
        timestamp => now - timestamp < 1000
      );

      // Check if we can make a request without exceeding rate limits
      if (this.recentRequests.length >= this.config.maxRequestsPerSecond) {
        // Wait for the oldest request to expire
        const oldestRequest = Math.min(...this.recentRequests);
        const waitTime = 1000 - (now - oldestRequest) + 10; // Add 10ms buffer

        if (waitTime > 0) {
          await this.sleep(waitTime);
          continue; // Re-check after waiting
        }
      }

      // Ensure minimum interval between requests
      const timeSinceLastRequest = now - this.lastRequestTime;
      const minInterval = 1000 / this.config.burstLimit;

      if (timeSinceLastRequest < minInterval) {
        const waitTime = minInterval - timeSinceLastRequest;
        await this.sleep(waitTime);
      }

      const request = this.requestQueue.shift()!;

      try {
        // Execute the request
        this.lastRequestTime = Date.now();
        this.recentRequests.push(this.lastRequestTime);

        const result = await request.execute();
        request.resolve(result);

      } catch (error: any) {
        // Handle 429 errors with exponential backoff
        if (error?.code === 429 || error?.status === 429 ||
            (error?.message && error.message.toLowerCase().includes('too many requests'))) {

          if (request.retryCount < this.config.retryAttempts) {
            request.retryCount++;

            // Exponential backoff: base * (2^retryCount) + random jitter
            const delay = this.config.retryDelayBase *
                         Math.pow(2, request.retryCount - 1) +
                         Math.random() * 1000;

            console.warn(
              `Rate limit hit, retrying in ${Math.round(delay)}ms (attempt ${request.retryCount}/${this.config.retryAttempts})`
            );

            // Wait before retrying
            await this.sleep(delay);

            // Add back to the beginning of the queue for immediate retry
            this.requestQueue.unshift(request);
            continue;
          } else {
            console.error(`Rate limit exceeded, failed after ${this.config.retryAttempts} attempts`);
            request.reject(new Error(`Rate limit exceeded after ${this.config.retryAttempts} attempts: ${error.message}`));
          }
        } else {
          // Non-rate-limit error, reject immediately
          request.reject(error);
        }
      }
    }

    this.isProcessing = false;
  }

  /**
   * Sleep for specified milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get current queue status for debugging
   */
  getStatus(): {
    queueLength: number;
    recentRequestCount: number;
    isProcessing: boolean;
    lastRequestTime: number;
  } {
    const now = Date.now();
    const recentCount = this.recentRequests.filter(
      timestamp => now - timestamp < 1000
    ).length;

    return {
      queueLength: this.requestQueue.length,
      recentRequestCount: recentCount,
      isProcessing: this.isProcessing,
      lastRequestTime: this.lastRequestTime,
    };
  }

  /**
   * Clear all pending requests
   */
  clearQueue(): void {
    this.requestQueue.forEach(request =>
      request.reject(new Error('Request queue cleared'))
    );
    this.requestQueue = [];
  }
}

// Default rate limiter instance for Infura
// Infura free tier allows ~100,000 requests per day, but we'll be very conservative
// to avoid 429 errors, especially during initial data loading
export const infuraRateLimiter = new RateLimiter({
  maxRequestsPerSecond: 2,  // Very conservative limit (was 8)
  burstLimit: 1,            // No bursts allowed (was 3)
  retryAttempts: 3,
  retryDelayBase: 5000,     // Start with 5 second delay for retries (was 2000)
});